import { setRiskConfigNameError } from '@Actions/Risk.actions';
import { selectRiskConfigNameError } from '@Selectors/Risk.selectors';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { useGlobalization } from '@Translations/useGlobalization';
import { RiskConfigCard } from '@innovyze/lib-am-common';
import {
	assetSchemaSelectorBySystemType,
	assetSchemaSelectorByType,
	getAllSystemsSchemaAction,
	getAssetSchemaBySystemTypeAction,
} from '@innovyze/lib_asset_schema_store';
import { MenuItem, SelectChangeEvent } from '@mui/material';
import { useSystemTypes } from '@Hooks/useSystemTypes';
import { useAssetSchemaFF } from '@Hooks/useAssetSchemaFF';

export interface UseRiskDetailsFormArgs {
	open: boolean;
	duplicate?: RiskConfigCard;
}

export const useRiskDetailsForm = ({
	open,
	duplicate,
}: UseRiskDetailsFormArgs) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const systemTypes = useSystemTypes();
	const { isValidAssetType } = useAssetSchemaFF();

	const [newName, setNewName] = useState('');
	const [systemType, setSystemType] = useState('');
	const [assetType, setAssetType] = useState('');
	const [assetFilterId, setAssetFilterId] = useState('');
	const [inspectionStandard, setInspectionStandard] = useState('');
	const [disableStandardTypes, setDisableStandardTypes] = useState(true);

	const configNameError = selectRiskConfigNameError();
	const assetSchemaByType = useSelector(state => {
		if (assetType) {
			return assetSchemaSelectorByType(state, assetType);
		}
	});

	useEffect(() => {
		dispatch(getAllSystemsSchemaAction());
	}, []);

	useEffect(() => {
		if (systemType) {
			dispatch(getAssetSchemaBySystemTypeAction(systemType));
		}
	}, [systemType]);

	useEffect(() => {
		if (assetType) {
			setDisableStandardTypes(
				assetSchemaByType?.getInspectionTypes().length === 0,
			);
		}
	}, [assetType]);

	const assetTypes = useSelector(state => {
		return assetSchemaSelectorBySystemType(state, systemType)?.filter(at =>
			isValidAssetType(at.systemType.id, at.id),
		);
	});

	useEffect(() => {
		setNewName(duplicate?.name ? `Copy of ${duplicate.name}` : '');
		dispatch(
			setRiskConfigNameError(
				duplicate?.name ? '' : t('Name is required'),
			),
		);
		setSystemType(duplicate?.systemType ?? '');
		setAssetType(duplicate?.assetType ?? '');
		setAssetFilterId(duplicate?.assetFilterId ?? '');
		setInspectionStandard(duplicate?.inspectionStandard ?? '');
		setDisableStandardTypes(true);
	}, [open, duplicate]);

	useEffect(() => {
		setInspectionStandard(duplicate?.inspectionStandard ?? '');
	}, [assetType, duplicate?.inspectionStandard]);

	const inspectionStandardOptions = useMemo(() => {
		const inspectionTypes: JSX.Element[] = [];
		if (assetSchemaByType?.hasInspectionType('PACP')) {
			inspectionTypes.push(
				<MenuItem
					key={InspectionStandardType.PACP}
					value={InspectionStandardType.PACP}>
					{t('PACP')}
				</MenuItem>,
			);
		}
		if (assetSchemaByType?.hasInspectionType('MACP')) {
			inspectionTypes.push(
				<MenuItem
					key={InspectionStandardType.MACP}
					value={InspectionStandardType.MACP}>
					{t('MACP')}
				</MenuItem>,
			);
		}
		if (assetSchemaByType?.hasInspectionType('WSA05')) {
			inspectionTypes.push(
				<MenuItem
					key={InspectionStandardType.WSA}
					value={InspectionStandardType.WSA}>
					{t('WSA05')}
				</MenuItem>,
			);
		}

		return inspectionTypes;
	}, [assetType]);

	const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let error = '';
		if (event.target && event.target.value !== '') {
			setNewName(event.target.value);
			if (!/^[a-zA-Z0-9 ]+$/.test(event.target.value)) {
				error = t('Only alphanumeric characters are allowed');
			}
			if (!/^.{1,50}$/.test(event.target.value)) {
				error = t('Name cannot contain more than 50 characters');
			}
		} else {
			setNewName('');
			error = t('Name is required');
		}
		dispatch(setRiskConfigNameError(error));
	};

	const onSystemTypeChange = (event: SelectChangeEvent<string>) => {
		setDisableStandardTypes(true);
		const target = event?.target as HTMLInputElement;
		if (target === undefined) {
			setSystemType('');
		} else {
			setSystemType(target.value);
			dispatch(getAssetSchemaBySystemTypeAction(target.value));
		}
	};

	const onAssetTypeChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		setAssetType(target.value);
		setAssetFilterId('');
	};

	const onAssetFilterChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		setAssetFilterId(target.value);
	};

	const onInspectionStandardChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		setInspectionStandard(target.value);
	};

	const isValid =
		configNameError.length === 0 &&
		systemType.length !== 0 &&
		assetType.length !== 0 &&
		!(
			inspectionStandardOptions.length !== 0 &&
			inspectionStandard.length === 0
		);

	return {
		newName,
		systemType,
		assetType,
		assetFilterId,
		inspectionStandard,
		disableStandardTypes,
		configNameError,
		systemTypes,
		assetTypes,
		inspectionStandardOptions,
		isValid,
		onNameChange,
		onSystemTypeChange,
		onAssetTypeChange,
		onAssetFilterChange,
		onInspectionStandardChange,
	};
};

export type RiskDetailsForm = ReturnType<typeof useRiskDetailsForm>;
